import { IBreadcrumbItem, PanelType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  iconButtonStylesNoBG,
  IPageHeaderProps,
  IPanelProps,
  PageHeader,
  pageHeaderStylesSecondary,
  Panel,
  useClassNames,
} from '@h2oai/ui-kit';
import { useEffect } from 'react';

import { ResourceType } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { AccessInfo, ContentsTitle, narrowPanelWidth } from '../../featurestore/models';
import { GrantPermission } from '../GrantPermission/GrantPermission';
import {
  grantPermissionPanelStylesDefault,
  IGrantPermissionPanelClassNames,
  IGrantPermissionPanelStyles,
} from './GrantPermissionPanel.styles';

const getBreadcrumbItems = (title = '', name?: string, isProject?: boolean): IBreadcrumbItem[] => {
  const text = isProject ? `${ContentsTitle.project}: ${name}` : `${ContentsTitle.featureSet}: ${name}`;
  return [
    { text: title, key: title },
    {
      text,
      key: text,
      isCurrentItem: true,
    },
  ];
};

const getTitle = (requestHigherPermission?: boolean, promote?: boolean) =>
  requestHigherPermission ? ContentsTitle.requestAccess : promote ? ContentsTitle.promote : ContentsTitle.grantAccess;

interface IGrantPermissionPanelProps {
  id?: string;
  name?: string;
  resourceType?: ResourceType;
  accessInfo?: AccessInfo;
  requestHigherPermission?: boolean;
  promote?: boolean;
  show: boolean;
  onDismiss: (updated?: boolean) => void;
}

export const GrantPermissionPanel = ({
  name = '',
  id,
  resourceType,
  accessInfo,
  requestHigherPermission,
  promote,
  show,
  onDismiss,
}: IGrantPermissionPanelProps) => {
  const [isOpen, { setTrue: open, setFalse: close }] = useBoolean(false);
  const panelProps: IPanelProps = {
    type: PanelType.custom,
    customWidth: narrowPanelWidth,
    isOpen,
    onDismiss: () => onDismiss(),
    isFooterAtBottom: true,
    hasCloseButton: false,
  };
  const title = getTitle(requestHigherPermission, promote);
  const pageHeaderProps: IPageHeaderProps = {
    pageTitle: title,
    styles: pageHeaderStylesSecondary,
    breadcrumbProps: {
      items: getBreadcrumbItems(title, name, resourceType === ResourceType.PROJECT),
    },
    actions: <IconButton styles={iconButtonStylesNoBG} iconName="Clear" onClick={() => onDismiss()} />,
  };

  useEffect(() => {
    if (show) {
      open();
    } else {
      close();
    }
  }, [show]);
  const classNames = useClassNames<IGrantPermissionPanelStyles, IGrantPermissionPanelClassNames>(
    'GrantPermissionPanel',
    grantPermissionPanelStylesDefault
  );
  return (
    <Panel {...panelProps}>
      <div className={classNames.root}>
        <PageHeader {...pageHeaderProps} />
        <GrantPermission
          id={id}
          resourceType={resourceType}
          requestHigherPermission={requestHigherPermission}
          promote={promote}
          accessInfo={accessInfo}
          title={title}
          onSave={() => onDismiss(true)}
          onCancel={onDismiss}
        />
      </div>
    </Panel>
  );
};
